import axios from "axios";
import API_ENDPOINTS from "../config/api";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  margin-top: 2rem;
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    flex: 1;
  }
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TileElement = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: 4px;
`;

const StatusBadge = styled.span<{ $isActive?: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background-color: ${(props) => (props.$isActive ? "#4caf50" : "#ffc107")};
  color: white;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#f8f9fa" : "#013566")};
  color: ${(props) => (props.$disabled ? "#6c757d" : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #012a4a;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PageInfo = styled.span`
  font-weight: 500;
  color: #013566;
  font-size: 0.9rem;
`;

const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const SkeletonElement = styled.div`
  width: 200px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const VerticalClients = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [verticalName, setVerticalName] = useState("");
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchVerticalClients = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await axios.get(API_ENDPOINTS.VERTICAL_CLIENTS(id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setClients(response.data?.data || []);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching vertical clients:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVerticalClients();
  }, [id, token]);

  // Fetch vertical name for display
  useEffect(() => {
    const fetchVerticalName = async () => {
      if (!id) return;

      try {
        const response = await axios.get(API_ENDPOINTS.VERTICALS, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const vertical = response.data?.find((v: any) => v.id === parseInt(id));
        if (vertical) {
          const capitalized =
            vertical.name.charAt(0).toUpperCase() + vertical.name.slice(1);
          setVerticalName(capitalized);
        }
      } catch (error) {
        console.error("Error fetching vertical name:", error);
      }
    };

    fetchVerticalName();
  }, [id, token]);

  // Calculate pagination
  const totalPages = Math.ceil(clients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClients = clients.slice(startIndex, endIndex);

  // Reset to page 1 when clients change
  useEffect(() => {
    setCurrentPage(1);
  }, [clients.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container>
      <PageTitle>
        {verticalName ? `${verticalName} Clients` : "Vertical Clients"}
      </PageTitle>
      <GoBack onClick={() => navigate("/verticals")}>
        ← Back to Verticals
      </GoBack>

      <Tile>
        <TileHeader>
          <h2>ID</h2>
          <h2>Client Name</h2>
          <h2>Legal Name</h2>
          <h2>Logo</h2>
          <h2>Status</h2>
        </TileHeader>

        <TileSection>
          {loading ? (
            // Skeleton loading state
            Array.from({ length: 5 }).map((_, index) => (
              <SkeletonRow key={index}>
                <SkeletonElement />
                <SkeletonElement />
                <SkeletonElement />
                <SkeletonElement />
              </SkeletonRow>
            ))
          ) : currentClients.length > 0 ? (
            currentClients.map((client) => (
              <TileRow key={client.id}>
                <TileElement>{client.id}</TileElement>
                <TileElement>{client.name || "N/A"}</TileElement>
                <TileElement>{client.legal_name || "N/A"}</TileElement>
                <TileElement>
                  {client.logo_url ? (
                    <LogoImage src={client.logo_url} alt={client.name} />
                  ) : (
                    "N/A"
                  )}
                </TileElement>
                <TileElement>
                  <StatusBadge $isActive={client.status === 1}>
                    {client.status === 1 ? "Active" : "Inactive"}
                  </StatusBadge>
                </TileElement>
              </TileRow>
            ))
          ) : (
            <TileRow>
              <TileElement style={{ width: "100%", textAlign: "center" }}>
                No clients found
              </TileElement>
            </TileRow>
          )}
        </TileSection>

        {!loading && clients.length > 0 && totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              $disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            <PageInfo>
              Page {currentPage} of {totalPages}
            </PageInfo>
            <PaginationButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              $disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationContainer>
        )}
      </Tile>
    </Container>
  );
};

export default VerticalClients;
